import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heart, Syringe, Zap, FlaskConical, Pill, Stethoscope,
  Scissors, HeartPulse, FileText, Play, Square, Copy, Download,
  MoreHorizontal, PlusCircle, Mic, MicOff, X, Users, Check,
} from "lucide-react";
import jsPDF from "jspdf";
import { toast, Toaster } from "sonner";

import {
  startCode, getCode, addCodeEvent, endCode, getCodeNarrative, deleteCodeEvent,
} from "../lib/codeApi";
import CodeTimer from "../components/CodeTimer";
import CodeEventButton, { THEMES } from "../components/CodeEventButton";
import CodeQuickPick from "../components/CodeQuickPick";
import CodeTimeline from "../components/CodeTimeline";
import { useFeatureTimer } from "../lib/analytics";

const LS_KEY = "prn_current_code_id";
const RESTART_CPR_DELAY_MS = 10000;

const formatElapsed = (ms) => {
  const s = Math.floor(Math.max(0, ms) / 1000);
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${ss}`;
};

// ---------- Voice dictation hook (Web Speech API) ----------
function useDictation(onResult) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice dictation not supported in this browser");
      return;
    }
    if (recRef.current) recRef.current.stop();
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      onResult(txt);
    };
    rec.onend = () => { setListening(false); recRef.current = null; };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== "no-speech" && e.error !== "aborted") {
        toast.error(`Mic error: ${e.error}`);
      }
    };
    rec.start();
    recRef.current = rec;
    setListening(true);
  };
  const stop = () => { if (recRef.current) recRef.current.stop(); };
  return { listening, supported, start, stop };
}

// ---------- Start Screen ----------
function StartScreen({ onStart }) {
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [interval, setIntervalMin] = useState(4);
  const [loading, setLoading] = useState(false);

  const begin = async () => {
    setLoading(true);
    try {
      const code = await startCode(location || null, interval * 60);
      localStorage.setItem(LS_KEY, code.id);
      onStart(code);
    } catch (e) {
      console.error("startCode failed", e);
      toast.error("Could not start code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070d1a] text-white flex flex-col">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-[#1f2c47] px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="code-back-home" className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-sky-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="flex items-center gap-2">
          <img src="/project-rn-logo.png" alt="" className="h-7 w-7 rounded-md object-cover" />
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-slate-400">Project RN</div>
            <div className="text-sm font-bold tracking-tight">Code Scribe</div>
          </div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-10">
        <div className="w-full max-w-xl text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-400 mb-3">
            Fast. Simple. Built for real codes.
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-3 leading-none">
            CODE <span className="text-sky-400">SCRIBE</span>
          </h1>
          <p className="text-sm text-slate-400 mb-10 max-w-md mx-auto leading-relaxed">
            Tap once. Start recording. Giant timers, one-tap event logging, auto timeline, PDF export. Built for the chaos.
          </p>
          <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl p-6 mb-6 text-left space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">Location (optional)</label>
              <input
                value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. ICU 12, ED Bay 3, Trauma 1"
                data-testid="code-location"
                className="w-full px-4 py-3 bg-[#152340] border border-[#2a3a5e] rounded-xl text-base focus:outline-none focus:border-sky-400"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">Epinephrine interval</label>
              <div className="flex gap-2">
                {[3, 4, 5].map((m) => (
                  <button key={m} onClick={() => setIntervalMin(m)} data-testid={`epi-int-${m}`}
                    className={`flex-1 py-3 rounded-xl border font-bold text-base transition-colors ${
                      interval === m ? "bg-sky-500 border-sky-500 text-white" : "bg-[#152340] border-[#2a3a5e] text-slate-300 hover:bg-[#1d2f56]"
                    }`}
                  >{m} min</button>
                ))}
              </div>
            </div>
          </div>
          <button onClick={begin} disabled={loading} data-testid="start-code-btn"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-5 rounded-2xl text-2xl font-black tracking-wider uppercase flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-50 shadow-[0_0_30px_rgba(16,185,129,0.35)]">
            <Play className="h-7 w-7" fill="currentColor" />
            {loading ? "Starting…" : "Start Code"}
          </button>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-3">Tap once. Start fast.</div>
        </div>
      </main>
    </div>
  );
}

// ---------- Note Input with Voice ----------
function NoteInput({ onSubmit, onCancel, autoFocus, placeholder }) {
  const [text, setText] = useState("");
  const ref = useRef(null);
  const { listening, supported, start, stop } = useDictation((t) => setText(t));
  useEffect(() => { if (autoFocus) setTimeout(() => ref.current?.focus(), 50); }, [autoFocus]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onSubmit(t);
    setText("");
  };

  return (
    <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl p-3 flex gap-2 items-center">
      <input
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder={placeholder || 'e.g. "Dr Smith arrived" / "Family updated"'}
        data-testid="note-input"
        className="flex-1 px-4 py-3 bg-[#152340] border border-[#2a3a5e] rounded-xl text-base focus:outline-none focus:border-sky-400"
      />
      {supported && (
        <button
          type="button"
          onClick={() => (listening ? stop() : start())}
          data-testid="note-voice-btn"
          title={listening ? "Stop dictation" : "Dictate note"}
          className={`p-3 rounded-xl border transition-colors ${
            listening
              ? "bg-red-500 border-red-500 text-white animate-pulse"
              : "bg-[#152340] border-[#2a3a5e] text-sky-300 hover:bg-[#1d2f56]"
          }`}
        >
          {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
      )}
      <button
        type="button"
        onClick={submit}
        data-testid="submit-note"
        className="px-5 py-3 bg-sky-500 hover:bg-sky-400 text-white font-bold rounded-xl uppercase tracking-wider text-sm"
      >Log</button>
      {onCancel && (
        <button type="button" onClick={onCancel} className="px-3 py-3 text-slate-400 hover:text-white">Cancel</button>
      )}
    </div>
  );
}

// ---------- Wrap-Up Modal (after END CODE) ----------
function WrapUpModal({ code, onLogEvent, onDelete, onFinalize }) {
  const [note, setNote] = useState("");
  const [personnel, setPersonnel] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const { listening, supported, start, stop } = useDictation((t) => setNote(t));

  const addNote = async () => {
    const t = note.trim();
    if (!t) return;
    try {
      await onLogEvent("note", t);
      setNote("");
      toast.success("Note added");
    } catch {
      /* error already toasted by log() */
    }
  };

  const addPersonnel = async () => {
    const t = personnel.trim();
    if (!t) return;
    try {
      await onLogEvent("personnel", `Personnel: ${t}`);
      setPersonnel("");
      toast.success("Personnel added");
    } catch {
      /* error already toasted by log() */
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      if (note.trim()) await onLogEvent("note", note.trim());
      if (personnel.trim()) await onLogEvent("personnel", `Personnel: ${personnel.trim()}`);
    } catch {
      /* keep going to finalize even if last flush failed */
    }
    onFinalize();
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 grid place-items-center px-4 py-8 overflow-y-auto" data-testid="wrap-up-modal">
      <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl w-full max-w-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="text-xs font-bold tracking-widest text-emerald-300 uppercase">Code Ended</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">Wrap Up</h2>
        <p className="text-sm text-slate-400 mt-1 mb-6">Add any final notes or personnel before finalizing the timeline + PDF.</p>

        {/* Final notes */}
        <div className="mb-5">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">Final notes</label>
          <div className="flex gap-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "Family at bedside", "Hand-off given to Dr. Smith"'
              rows={2}
              data-testid="wrap-note-input"
              className="flex-1 px-4 py-3 bg-[#152340] border border-[#2a3a5e] rounded-xl text-base focus:outline-none focus:border-sky-400 resize-none"
            />
            {supported && (
              <button
                type="button"
                onClick={() => (listening ? stop() : start())}
                data-testid="wrap-note-voice"
                className={`p-3 rounded-xl border transition-colors self-start ${
                  listening
                    ? "bg-red-500 border-red-500 text-white animate-pulse"
                    : "bg-[#152340] border-[#2a3a5e] text-sky-300 hover:bg-[#1d2f56]"
                }`}
                title={listening ? "Stop dictation" : "Dictate note"}
              >
                {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
            )}
            <button
              type="button"
              onClick={addNote}
              data-testid="wrap-note-add"
              className="px-4 py-3 bg-sky-500 hover:bg-sky-400 text-white font-bold rounded-xl text-sm uppercase tracking-wider self-start"
            >Add</button>
          </div>
        </div>

        {/* Personnel */}
        <div className="mb-6">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5 flex items-center gap-1.5">
            <Users className="h-3 w-3" /> Personnel present
          </label>
          <div className="flex gap-2">
            <input
              value={personnel}
              onChange={(e) => setPersonnel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addPersonnel()}
              placeholder='e.g. "Dr. Patel (intensivist), RT Jones, RN Lee"'
              data-testid="wrap-pers-input"
              className="flex-1 px-4 py-3 bg-[#152340] border border-[#2a3a5e] rounded-xl text-base focus:outline-none focus:border-sky-400"
            />
            <button
              type="button"
              onClick={addPersonnel}
              data-testid="wrap-pers-add"
              className="px-4 py-3 bg-sky-500 hover:bg-sky-400 text-white font-bold rounded-xl text-sm uppercase tracking-wider"
            >Add</button>
          </div>
        </div>

        {/* Full timeline with delete */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
            Timeline · review and edit
          </div>
          <CodeTimeline
            events={code.events}
            onDelete={onDelete}
            maxHeightClass="max-h-56"
          />
        </div>

        <button
          onClick={finalize}
          disabled={finalizing}
          data-testid="finalize-code"
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-4 rounded-2xl text-base font-black tracking-wider uppercase flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          <Check className="h-5 w-5" />
          {finalizing ? "Finalizing…" : "Finalize & View Summary"}
        </button>
      </div>
    </div>
  );
}

// ---------- Text Prompt Modal (replaces window.prompt for "Other ...") ----------
function TextPromptModal({ open, title, hint, placeholder, submitLabel = "Log", onSubmit, onClose }) {
  const [value, setValue] = useState("");
  const ref = useRef(null);
  const { listening, supported, start, stop } = useDictation((t) => setValue(t));

  useEffect(() => {
    if (open) {
      setValue("");
      setTimeout(() => ref.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const t = value.trim();
    if (!t) return;
    onSubmit(t);
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[60] grid place-items-center px-4"
      onClick={onClose}
      data-testid="text-prompt-modal"
    >
      <div
        className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 mb-1">Quick Pick</div>
        <h3 className="text-2xl font-black tracking-tight text-white mb-1">{title}</h3>
        {hint && <p className="text-xs text-slate-400 mb-4">{hint}</p>}
        <div className="flex gap-2 mb-4">
          <input
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder={placeholder}
            data-testid="text-prompt-input"
            className="flex-1 px-4 py-3 bg-[#152340] border border-[#2a3a5e] rounded-xl text-base focus:outline-none focus:border-sky-400"
          />
          {supported && (
            <button
              type="button"
              onClick={() => (listening ? stop() : start())}
              data-testid="text-prompt-voice"
              title={listening ? "Stop dictation" : "Dictate"}
              className={`p-3 rounded-xl border transition-colors ${
                listening
                  ? "bg-red-500 border-red-500 text-white animate-pulse"
                  : "bg-[#152340] border-[#2a3a5e] text-sky-300 hover:bg-[#1d2f56]"
              }`}
            >
              {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            data-testid="text-prompt-cancel"
            className="flex-1 px-4 py-3 bg-[#152340] hover:bg-[#1d2f56] border border-[#2a3a5e] text-white rounded-xl font-bold uppercase tracking-wider text-sm"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={!value.trim()}
            data-testid="text-prompt-submit"
            className="flex-1 px-4 py-3 bg-sky-500 hover:bg-sky-400 text-white rounded-xl font-bold uppercase tracking-wider text-sm disabled:opacity-50"
          >{submitLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Live Recorder ----------
function LiveRecorder({ code, refresh, onEnded }) {
  const [pulseKey, setPulseKey] = useState(0);
  const [epiKey, setEpiKey] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [picker, setPicker] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const [wrapUp, setWrapUp] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [textPrompt, setTextPrompt] = useState(null); // { title, hint, placeholder, onSubmit }
  const restartCprTimerRef = useRef(null);

  const startMs = useMemo(() => new Date(code.started_at).getTime(), [code.started_at]);
  const ended = !!code.ended_at;
  const endMs = useMemo(() => (ended ? new Date(code.ended_at).getTime() : null), [ended, code.ended_at]);

  useEffect(() => {
    if (ended) return;
    const i = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(i);
  }, [ended]);

  useEffect(() => () => { if (restartCprTimerRef.current) clearTimeout(restartCprTimerRef.current); }, []);

  const epiCount = code.events.filter((e) => e.type === "epi").length;
  const elapsed = formatElapsed((endMs || now) - startMs);

  const log = async (type, label, meta) => {
    try {
      await addCodeEvent(code.id, { type, label, meta: meta || {} });
      await refresh();
      return true;
    } catch (e) {
      toast.error("Failed to log event");
      throw e;
    }
  };

  const handleDelete = async (ev) => {
    try {
      await deleteCodeEvent(code.id, ev.id);
      await refresh();
      toast.success("Event deleted");
    } catch (e) {
      console.error("deleteCodeEvent failed", e);
      toast.error("Could not delete event");
    }
  };

  const scheduleRestartCpr = () => {
    if (restartCprTimerRef.current) clearTimeout(restartCprTimerRef.current);
    restartCprTimerRef.current = setTimeout(() => {
      log("cpr_restart", "Restart CPR").catch(() => {});
      restartCprTimerRef.current = null;
    }, RESTART_CPR_DELAY_MS);
  };

  // --- Button handlers ---
  const handlePulseCheck = () => {
    setPulseKey((k) => k + 1); // restart timer INSTANTLY on tap
    log("pulse_check", "Pulse Check"); // fire-and-forget
    setPicker({
      kind: "rhythm",
      title: "Rhythm",
      hint: "10-second assessment · CPR restarts in 10s",
      options: [
        { id: "vf", label: "VF" },
        { id: "vt", label: "Pulseless VT" },
        { id: "pea", label: "PEA" },
        { id: "asystole", label: "Asystole" },
        { id: "organized", label: "Organized Rhythm" },
        { id: "other_r", label: "Other" },
      ],
      onPick: async (opt) => {
        await log("rhythm", opt.label);
        setPicker(null);
        scheduleRestartCpr();
      },
      onCloseExtra: () => scheduleRestartCpr(),
    });
  };

  const handleEpiTimer = () => {
    setEpiKey((k) => k + 1);
    log("epi", "Epinephrine 1 mg").then(() => {
      toast.success(`EPI #${epiCount + 1} LOGGED`, { duration: 1500 });
    });
  };

  const quickEpi = () => {
    setEpiKey((k) => k + 1);
    log("epi", "Epinephrine 1 mg").then(() => {
      toast.success(`EPI #${epiCount + 1} LOGGED`, { duration: 1500 });
    });
  };

  const openShock = () => setPicker({
    kind: "shock", title: "Shock",
    options: [
      { id: "200", label: "200 J" }, { id: "300", label: "300 J" },
      { id: "360", label: "360 J" }, { id: "custom", label: "Custom" },
    ],
    onPick: async (opt) => {
      if (opt.id === "custom") {
        setPicker(null);
        setTextPrompt({
          title: "Custom Shock",
          hint: "Enter joules delivered.",
          placeholder: 'e.g. "150", "250"',
          submitLabel: "Log Shock",
          onSubmit: async (txt) => {
            const j = txt.replace(/[^0-9.]/g, "");
            const label = j ? `Shock ${j} J` : `Shock ${txt}`;
            try { await log("shock", label); } catch (_) { /* error toasted in log() */ }
            setTextPrompt(null);
          },
        });
        return;
      }
      await log("shock", `Shock ${opt.label}`);
      setPicker(null);
    },
  });

  const openAmio = () => setPicker({
    kind: "amio", title: "Amiodarone",
    options: [{ id: "150", label: "150 mg" }, { id: "300", label: "300 mg" }],
    onPick: async (opt) => { await log("amiodarone", `Amiodarone ${opt.label}`); setPicker(null); },
  });
  const openBicarb = () => setPicker({
    kind: "bicarb", title: "Bicarb",
    options: [{ id: "50", label: "50 mEq" }, { id: "100", label: "100 mEq" }],
    onPick: async (opt) => { await log("bicarb", `Bicarb ${opt.label}`); setPicker(null); },
  });
  const openCalcium = () => setPicker({
    kind: "calcium", title: "Calcium",
    options: [
      { id: "chloride", label: "Calcium Chloride 1 g" },
      { id: "gluconate", label: "Calcium Gluconate 1 g" },
    ],
    onPick: async (opt) => { await log("calcium", opt.label); setPicker(null); },
  });
  const openMag = () => setPicker({
    kind: "mag", title: "Magnesium",
    options: [{ id: "1", label: "1 g" }, { id: "2", label: "2 g" }],
    onPick: async (opt) => { await log("magnesium", `Magnesium ${opt.label}`); setPicker(null); },
  });
  const openProc = () => setPicker({
    kind: "proc", title: "Procedure",
    options: [
      { id: "ct", label: "Chest Tube" }, { id: "cl", label: "Central Line" },
      { id: "al", label: "Arterial Line" }, { id: "nd", label: "Needle Decompression" },
      { id: "pc", label: "Pericardiocentesis" }, { id: "ot", label: "Other Procedure" },
    ],
    onPick: async (opt) => {
      if (opt.id === "ot") {
        setPicker(null);
        setTextPrompt({
          title: "Other Procedure",
          hint: "Describe the procedure performed.",
          placeholder: 'e.g. "REBOA balloon placement", "Thoracotomy"',
          submitLabel: "Log Procedure",
          onSubmit: async (txt) => {
            try { await log("procedure", txt); } catch (_) { /* error toasted in log() */ }
            setTextPrompt(null);
          },
        });
        return;
      }
      await log("procedure", opt.label);
      setPicker(null);
    },
  });
  const openIntub = () => { log("intubation", "Intubation").catch(() => {}); };
  const handleROSC = () => { log("rosc", "ROSC").then(() => toast.success("ROSC LOGGED")).catch(() => {}); };
  const openMore = () => setPicker({
    kind: "more", title: "More Meds",
    options: [
      { id: "lido", label: "Lidocaine 1 mg/kg" },
      { id: "atropine", label: "Atropine 1 mg" },
      { id: "narcan", label: "Naloxone 0.4 mg" },
      { id: "vaso", label: "Vasopressin 40 U" },
      { id: "d50", label: "D50W 25 g" },
      { id: "ntg", label: "Other Medication" },
    ],
    onPick: async (opt) => {
      if (opt.id === "ntg") {
        setPicker(null);
        setTextPrompt({
          title: "Other Medication",
          hint: "Name the medication or drip started.",
          placeholder: 'e.g. "Levophed 5 mcg/min", "Push-dose epi 10 mcg"',
          submitLabel: "Log Med",
          onSubmit: async (txt) => {
            try { await log("med_other", txt); } catch (_) { /* error toasted in log() */ }
            setTextPrompt(null);
          },
        });
        return;
      }
      await log("med_other", opt.label);
      setPicker(null);
    },
  });

  const handleEnd = async () => {
    setConfirmEnd(false);
    setEnding(true);
    if (restartCprTimerRef.current) clearTimeout(restartCprTimerRef.current);
    try {
      await endCode(code.id);
      await refresh();
      localStorage.removeItem(LS_KEY);
      setWrapUp(true);
    } catch (e) {
      console.error("endCode failed", e);
      toast.error("Failed to end code");
    } finally {
      setEnding(false);
    }
  };

  const epiSub = useMemo(() => {
    const interval = code.epi_interval_sec;
    return `Next dose in ${String(Math.floor(interval / 60)).padStart(2, "0")}:${String(interval % 60).padStart(2, "0")} (${interval / 60} min)`;
  }, [code.epi_interval_sec]);

  return (
    <div className="min-h-screen bg-[#070d1a] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-[#1f2c47] px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${ended ? "bg-slate-500" : "bg-red-500 animate-pulse"}`} />
            <span className={`text-xs font-bold tracking-widest uppercase ${ended ? "text-slate-400" : "text-red-400"}`}>
              {ended ? "Code Ended" : "Code In Progress"}
            </span>
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Elapsed Time</div>
          <div className="font-mono text-3xl sm:text-4xl font-black tabular-nums tracking-tight text-white" data-testid="code-elapsed">
            {elapsed}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {code.location && (
            <span className="font-mono text-slate-300 bg-[#152340] border border-[#2a3a5e] px-2 py-1 rounded uppercase tracking-wider">
              {code.location}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <CodeTimer
              title="PULSE CHECK"
              durationSec={120}
              resetKey={pulseKey}
              onTap={handlePulseCheck}
              subtitle="Tap to restart · 10s rhythm prompt"
              icon={<HeartPulse className="h-4 w-4 text-rose-400" strokeWidth={2.2} />}
              testid="pulse-timer"
              frozen={ended}
            />
            <CodeTimer
              title="EPINEPHRINE"
              durationSec={code.epi_interval_sec}
              resetKey={epiKey}
              onTap={handleEpiTimer}
              subtitle={epiSub}
              icon={<Syringe className="h-4 w-4 text-emerald-400" strokeWidth={2.2} />}
              badge={`EPI #${epiCount + 1}`}
              testid="epi-timer"
              frozen={ended}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            <CodeEventButton label="EPI"        dose="1 mg"  theme={THEMES.green}  Icon={Syringe}        onTap={ended ? undefined : quickEpi}    testid="btn-epi" />
            <CodeEventButton label="SHOCK"      dose="200 J" theme={THEMES.orange} Icon={Zap}            onTap={ended ? undefined : openShock}   testid="btn-shock" />
            <CodeEventButton label="AMIO"       dose="300 mg" theme={THEMES.purple} Icon={FlaskConical}  onTap={ended ? undefined : openAmio}    testid="btn-amio" />
            <CodeEventButton label="BICARB"     dose="50 mEq" theme={THEMES.teal}   Icon={Pill}          onTap={ended ? undefined : openBicarb}  testid="btn-bicarb" />
            <CodeEventButton label="CALCIUM"    dose="1 g"    theme={THEMES.yellow} Icon={PlusCircle}    onTap={ended ? undefined : openCalcium} testid="btn-calcium" />
            <CodeEventButton label="MAG"        dose="2 g"    theme={THEMES.cyan}   Icon={Pill}          onTap={ended ? undefined : openMag}     testid="btn-mag" />
            <CodeEventButton label="INTUBATION"               theme={THEMES.sky}    Icon={Stethoscope}   onTap={ended ? undefined : openIntub}   testid="btn-intub" />
            <CodeEventButton label="PROCEDURE"                theme={THEMES.gray}   Icon={Scissors}      onTap={ended ? undefined : openProc}    testid="btn-proc" />
            <CodeEventButton label="ROSC"                     theme={THEMES.red}    Icon={Heart}         onTap={ended ? undefined : handleROSC}  testid="btn-rosc" />
            <CodeEventButton label="MORE"                     theme={THEMES.gray}   Icon={MoreHorizontal} onTap={ended ? undefined : openMore}   testid="btn-more" />
          </div>

          {!ended && (!noteOpen ? (
            <button
              onClick={() => setNoteOpen(true)}
              data-testid="open-note"
              className="w-full flex items-center justify-center gap-2 py-4 bg-[#0e1a30] border border-[#1f2c47] hover:border-[#3a4a6e] rounded-2xl text-slate-300 text-sm font-bold uppercase tracking-widest transition-colors"
            >
              <FileText className="h-4 w-4" strokeWidth={2} />
              Add Note <span className="hidden sm:inline text-slate-500 normal-case tracking-normal font-normal text-xs">· tap mic to dictate</span>
            </button>
          ) : (
            <NoteInput
              autoFocus
              onSubmit={async (t) => { await log("note", t); setNoteOpen(false); }}
              onCancel={() => setNoteOpen(false)}
            />
          ))}

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center bg-[#0e1a30] border border-[#1f2c47] rounded-2xl py-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Shocks</div>
              <div className="font-mono text-2xl font-black text-amber-400">{code.events.filter(e => e.type === "shock").length}</div>
            </div>
            <div className="text-center bg-[#0e1a30] border border-[#1f2c47] rounded-2xl py-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Epi Doses</div>
              <div className="font-mono text-2xl font-black text-emerald-400">{epiCount}</div>
            </div>
            <button
              onClick={ended ? () => setWrapUp(true) : () => setConfirmEnd(true)}
              disabled={ending}
              data-testid="end-code"
              className="bg-red-600 hover:bg-red-500 text-white rounded-2xl font-black tracking-wider uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <Square className="h-4 w-4" fill="currentColor" />
              {ending ? "Ending…" : ended ? "Wrap Up" : "End Code"}
            </button>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <CodeTimeline events={code.events} onDelete={handleDelete} />
        </aside>
      </main>

      <CodeQuickPick
        open={!!picker}
        title={picker?.title || ""}
        hint={picker?.hint}
        options={picker?.options || []}
        onPick={picker?.onPick || (() => {})}
        onClose={() => {
          if (picker?.onCloseExtra) picker.onCloseExtra();
          setPicker(null);
        }}
      />

      {wrapUp && (
        <WrapUpModal
          code={code}
          onLogEvent={async (type, label) => { await log(type, label); }}
          onDelete={handleDelete}
          onFinalize={() => onEnded(code)}
        />
      )}

      <TextPromptModal
        open={!!textPrompt}
        title={textPrompt?.title || ""}
        hint={textPrompt?.hint}
        placeholder={textPrompt?.placeholder}
        submitLabel={textPrompt?.submitLabel}
        onSubmit={textPrompt?.onSubmit || (() => {})}
        onClose={() => setTextPrompt(null)}
      />

      {confirmEnd && (
        <div className="fixed inset-0 bg-black/85 z-50 grid place-items-center px-4" data-testid="confirm-end-modal">
          <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-bold tracking-widest text-red-300 uppercase">End Code?</span>
            </div>
            <h3 className="text-xl font-black tracking-tight text-white mb-1">Stop the timers?</h3>
            <p className="text-sm text-slate-400 mb-5">You can still add final notes and personnel before finalizing the timeline + PDF.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmEnd(false)}
                data-testid="confirm-end-cancel"
                className="flex-1 px-4 py-3 bg-[#152340] hover:bg-[#1d2f56] border border-[#2a3a5e] text-white rounded-xl font-bold uppercase tracking-wider text-sm"
              >Cancel</button>
              <button
                onClick={handleEnd}
                disabled={ending}
                data-testid="confirm-end-yes"
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold uppercase tracking-wider text-sm disabled:opacity-50"
              >{ending ? "Ending…" : "End Code"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Summary Screen ----------
function SummaryScreen({ code, onNew }) {
  const navigate = useNavigate();
  const [narrative, setNarrative] = useState("");
  const [summary, setSummary] = useState(null);
  const [dateLabel, setDateLabel] = useState("");
  const [tzLabel, setTzLabel] = useState("");

  useEffect(() => {
    getCode(code.id).then((fresh) => {
      getCodeNarrative(fresh.id).then((d) => {
        setNarrative(d.narrative);
        setSummary(d.summary);
        setDateLabel(d.date_label || "");
        setTzLabel(d.tz_label || "");
      });
    }).catch(() => toast.error("Failed to load summary"));
  }, [code.id]);

  const copyNarrative = async () => {
    await navigator.clipboard.writeText(narrative);
    toast.success("Narrative copied");
  };

  const downloadPdf = async () => {
    if (!summary) return;
    const fresh = await getCode(code.id);
    const doc = new jsPDF();
    let y = 16;
    doc.setFontSize(18); doc.setFont("helvetica", "bold");
    doc.text("Project RN — Code Scribe Summary", 14, y); y += 8;
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text("Educational simulation only. Not medical advice.", 14, y); y += 6;
    // Date + timezone banner — same source of truth as the timeline + narrative.
    if (dateLabel || tzLabel) {
      doc.setFont("helvetica", "bold");
      doc.text(`${dateLabel}${dateLabel && tzLabel ? "  ·  " : ""}${tzLabel ? "Times shown in " + tzLabel : ""}`, 14, y);
      y += 6;
    }
    doc.setDrawColor(200); doc.line(14, y, 196, y); y += 6;

    const row = (k, v) => { doc.setFont("helvetica","bold"); doc.text(`${k}:`, 14, y); doc.setFont("helvetica","normal"); doc.text(String(v ?? "—"), 60, y); y += 6; };
    row("Location", summary.location || "—");
    row("Code Start", summary.code_start || "—");
    row("Code End", summary.code_end || "—");
    row("Total Duration", summary.duration);
    row("Total Epi Doses", summary.total_epi_doses);
    row("Total Shocks", summary.total_shocks);
    row("Time to ROSC", summary.rosc_elapsed || "—");
    row("ROSC At", summary.rosc_time || "—");
    row("Rhythms", summary.rhythms_encountered.join(", ") || "—");
    const personnel = fresh.events.filter((e) => e.type === "personnel").map((e) => e.label.replace(/^Personnel:\s*/, ""));
    if (personnel.length) row("Personnel", personnel.join("; "));
    y += 2;

    doc.setFont("helvetica","bold"); doc.text("Timeline", 14, y); y += 6;
    doc.setFont("courier","normal"); doc.setFontSize(9);
    fresh.events.forEach((e) => {
      // Use the browser's local timezone — matches summary + timeline on-screen.
      const t = new Date(e.ts).toLocaleTimeString("en-US", { hour12: false });
      const line = `${t}   ${e.label}`;
      if (y > 280) { doc.addPage(); y = 16; }
      doc.text(line, 14, y); y += 5;
    });

    if (narrative) {
      if (y > 250) { doc.addPage(); y = 16; }
      y += 6;
      doc.setFont("helvetica","bold"); doc.setFontSize(11);
      doc.text("Narrative", 14, y); y += 6;
      doc.setFont("helvetica","normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(narrative, 180);
      lines.forEach((ln) => { if (y > 280) { doc.addPage(); y = 16; } doc.text(ln, 14, y); y += 5; });
    }

    const stamp = new Date(code.started_at).toISOString().slice(0,16).replace(/[:T]/g,"-");
    doc.save(`code-scribe-${stamp}.pdf`);
    toast.success("PDF downloaded");
  };

  return (
    <div className="min-h-screen bg-[#070d1a] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-[#1f2c47] px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="summary-home" className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-sky-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Home
        </button>
        <span className="text-sm font-bold tracking-widest text-emerald-400 uppercase">Code Finalized</span>
        <button onClick={onNew} data-testid="new-code" className="text-xs uppercase tracking-widest text-slate-400 hover:text-sky-300">New Code</button>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {(dateLabel || tzLabel) && (
          <div className="text-xs uppercase tracking-widest text-slate-400 font-mono" data-testid="summary-date-tz">
            {dateLabel}{dateLabel && tzLabel ? " · " : ""}{tzLabel ? `Times shown in ${tzLabel}` : ""}
          </div>
        )}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Duration" value={summary.duration} />
            <Stat label="Epi Doses" value={summary.total_epi_doses} color="text-emerald-400" />
            <Stat label="Shocks" value={summary.total_shocks} color="text-amber-400" />
            <Stat label="Time to ROSC" value={summary.rosc_elapsed || "—"} color="text-rose-400" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CodeTimeline events={code.events} />
          <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold tracking-widest text-sky-300 uppercase">Narrative</span>
              <button onClick={copyNarrative} data-testid="copy-narrative" className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-sky-400 hover:text-white">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="narrative-text">
              {narrative || "Generating…"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={downloadPdf} data-testid="export-pdf" className="bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-xl font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Download className="h-4 w-4" /> Export PDF
          </button>
          <button onClick={copyNarrative} className="bg-[#152340] hover:bg-[#1d2f56] border border-[#2a3a5e] text-white px-6 py-3 rounded-xl font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Copy className="h-4 w-4" /> Copy Narrative
          </button>
          <button onClick={onNew} className="bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-3 rounded-xl font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Play className="h-4 w-4" fill="currentColor" /> New Code
          </button>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl p-4 text-center">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`font-mono text-2xl font-black mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// ---------- Top-level ----------
export default function CodeScribe() {
  useFeatureTimer("code_scribe");
  const [code, setCode] = useState(null);
  const [view, setView] = useState("loading");

  useEffect(() => {
    const existing = localStorage.getItem(LS_KEY);
    if (!existing) {
      setView("start");
      return;
    }
    getCode(existing)
      .then((c) => {
        if (c.ended_at) {
          localStorage.removeItem(LS_KEY);
          setView("start");
        } else {
          setCode(c);
          setView("live");
        }
      })
      .catch(() => { localStorage.removeItem(LS_KEY); setView("start"); });
  }, []);

  const refresh = async () => {
    if (!code) return;
    const c = await getCode(code.id);
    setCode(c);
  };

  if (view === "loading") {
    return <div className="min-h-screen bg-[#070d1a] grid place-items-center text-slate-400 font-mono text-sm">Loading…</div>;
  }
  if (view === "start") {
    return <StartScreen onStart={(c) => { setCode(c); setView("live"); }} />;
  }
  if (view === "live" && code) {
    return (
      <LiveRecorder
        code={code}
        refresh={refresh}
        onEnded={async (c) => { const fresh = await getCode(c.id); setCode(fresh); setView("summary"); }}
      />
    );
  }
  if (view === "summary" && code) {
    return <SummaryScreen code={code} onNew={() => { setCode(null); setView("start"); }} />;
  }
  return null;
}
